/**
 * TokenDetailsPage — the /token/:mint page. Chart and buy-panel high up the
 * fold, a compact safety strip in the hero (instead of a giant verdict box),
 * and trading stats (mcap, holders, top-holder %) surfaced prominently.
 * Below-the-fold: holders, social, shill tweets, risk details.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import GatedLink from './GatedLink';
import Header from './Header';
import Footer from './Footer';
import SwapPanel from './SwapPanel';
import CopyButton from './CopyButton';
import ShareButton from './ShareButton';
import FigureBadge from './FigureBadge';
import RefreshModal from './RefreshModal';
import SocialResearchSummary from './SocialResearchSummary';
import ShillTweets from './ShillTweets';
import SocialMetaTags from './SocialMetaTags';
import { apiFetch, getApiHost } from '../utils/api';
import { trackEvent } from '../utils/analytics';
import { getRiskColor } from '../utils/riskUtils';
import { redirectToPhantomIfMobile } from '../utils/phantomMobile';
import { persistentPhantomWalletMatches } from '../utils/phantomDeeplink';
import { hasLiveWCSessionSync } from '../utils/walletConnectMobile';
import { TokenIcon } from './TokenIcon';
import { useAuth } from '../contexts/AuthContext';
import { EVM_CHAIN_INFO, ChainCandidate, EvmChainId, evmChartUrl, holderAnalysisPath, isEvmAddress, parseEvmChain, tokenPath, walletPath } from '../utils/chains';

interface TokenSafety {
  mint: string;
  chain?: string;              // EVM tokens only; Solana records have no chain
  timestamp: string;
  overallSafetyLevel: 'OK' | 'SAFE' | 'RISKY' | 'DANGEROUS';
  criticalRisks: string[];
  warnings: string[];
  infoMessages?: string[];
  hasSocialResearch?: boolean;   // whether full research (incl. social) has run
  tokenName: string;
  tokenSymbol: string;
  metadata?: { image?: string; description?: string; twitter?: string; telegram?: string; website?: string };
  platform?: string;
  creator?: string;
  creatorTokenCount?: number;
  creatorFigure?: { figureKey: string; displayName: string; trustLevel: string | null; confidence?: 'confirmed' | 'probable' } | null;
  creatorLabel?: string | null;
  figureHolders?: Array<{ figureKey: string; displayName: string; trustLevel: string | null; confidence?: 'confirmed' | 'probable'; percentage: number; sent?: boolean; tokenAccount?: string }>;
  tokenCreationDate?: string;
  totalHolders: number;
  topHolderOwnership: number;
  topTenOwnership: number;
  topNetworkOwnership: number;
  topNetworkWalletCount: number;
  topNetworkBundlerName: string | null;
  // Pre-computed risk levels from the worker — already factor in
  // locked/staked supply via the "adjusted" percentage path, so
  // prefer these over recomputing thresholds in the UI.
  topHolderRiskLevel?: 'EXTREME' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  topTenHoldersRiskLevel?: 'EXTREME' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  topNetworkRiskLevel?: 'EXTREME' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  liquidityAnalysis: string;
  totalLiquidityUsd?: number;  // EVM analyzer: summed DEX pool liquidity
  isMintable: boolean;
  isFreezable: boolean;
  isOnWatchlist?: boolean;
  watchlistedAt?: string | null;
  watchlistedAtMcapUsd?: number | null;   // birdeye snapshot or derived from current mcap × price ratio
  watchlistMultiplier?: number | null;    // precise SOL/SOL ratio (= mcap multiplier under constant supply)
  isSpotlighted?: boolean;
  isNew?: boolean;
  researchGated?: boolean;
  burnPercent?: number | null;
  ruggerHoldingsPct?: number | null;
  charity?: {
    name?: string;
    donationSource?: string;
    // Rich enrichment fields — populated by the on-chain
    // charity-enrichment pipeline. Older rows may have only name +
    // donationSource (legacy donate.gg scraper).
    website?: string | null;
    logo?: string | null;
    country?: string | null;
    donationSharePct?: number | null;     // % of creator fees routed to charity (usually 100)
    totalDonatedSol?: number | null;      // lifetime SOL donated through the sharing-config
    adminRevoked?: boolean | null;        // creator can no longer change the recipient
    beneficiaryWallet?: string | null;    // donation pool wallet (donate.gg-controlled)
    sharingConfigPda?: string | null;     // on-chain config account
    charities?: Array<{ name: string; slug?: string; website?: string; logo?: string; country?: string; weight: number }> | null;
  } | null;
  totalStreamflowLockedPercent?: number;
  totalTibaneStakedPercent?: number;
  totalMultisigPercent?: number;
  multisigVaultCount?: number;
  multisigThreshold?: number;
  multisigMemberCount?: number;
}

// Only HIGH/EXTREME gets red — MEDIUM is a soft yellow hint. LOW
// stays the default white so the numbers don't read as risks when
// they're well within normal range. Matches the worker's rules,
// and importantly uses the pre-adjusted percentages the worker
// stores (which exclude locked/staked supply) so a token that is
// partly locked doesn't falsely trip the red threshold.
const statColor = (level: string | null | undefined): string | undefined => {
  if (level === 'HIGH' || level === 'EXTREME') return 'text-crypto-red';
  if (level === 'MEDIUM') return 'text-crypto-yellow';
  return undefined;
};

// USD formatter for market-cap-scale numbers — K/M/B abbreviations.
function formatUsdMcap(n: number): string {
  if (!isFinite(n) || n <= 0) return '$0';
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000)     return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)         return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

const TokenDetailsPage: React.FC<{ chain?: EvmChainId }> = ({ chain: routeChain }) => {
  const { mint } = useParams<{ mint: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // EVM tokens live at /token/<chain>/<0x…> (routes are per chain, see App.tsx).
  // A bare /token/0x… resolves which chains the address trades on: one → redirect
  // to its canonical path, several → ask. Legacy ?chain= links redirect too.
  const isEvm = isEvmAddress(mint);
  const chainParam = isEvm ? (routeChain ?? null) : null;
  const legacyChain = isEvm && !routeChain ? parseEvmChain(searchParams.get('chain')) : null;
  const chainQs = chainParam ? `?chain=${chainParam}` : '';
  const [chainCandidates, setChainCandidates] = useState<ChainCandidate[] | null>(null);
  const { isAuthenticated, user, refreshUser, openLogin } = useAuth();
  const [token, setToken] = useState<TokenSafety | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A valid-but-unresearched token: we auto-run a quick (no-social) analysis and
  // poll the request status rather than showing "no data". analyzeError holds a
  // terminal reason the analysis can't complete (e.g. too few holders to score).
  const [unresearched, setUnresearched] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const autoResearchRef = useRef(false);
  const [requestingResearch, setRequestingResearch] = useState(false);
  // RefreshModal wiring: once the system_request is created, the modal
  // polls its status and shows progress until the worker reports
  // success/error.
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [previousCredits, setPreviousCredits] = useState<number | null>(null);
  // Positive-signal supply summary: locked % (Streamflow vesting) and
  // staked % (Tibane). Surfaced as hero pills when present so users
  // can see them at a glance without scrolling to the dedicated
  // sections lower on the page.
  const [lockedPercent, setLockedPercent] = useState<number | null>(null);
  const [stakedPercent, setStakedPercent] = useState<number | null>(null);

  // Mobile-only "focused trade" mode: by default mobile shows just the
  // chart + a Trade CTA below it; tapping the CTA reveals the SwapPanel
  // and hides everything else on the page so the user is in a
  // chart-on-top, swap-below view. Desktop ignores this flag entirely.
  // Initialised to true if the page lands at #swap (e.g. after the
  // mobile Phantom redirect bounces back into Phantom's webview), so
  // the user lands directly in the trade UI.
  const [mobileTradeOpen, setMobileTradeOpen] = useState<boolean>(
    () => typeof window !== 'undefined' && window.location.hash === '#swap',
  );

  // Triggers a full safety + social research re-run for this token.
  // Costs 1 credit (POST /api/request-token-safety-analysis).
  // After the request is accepted, opens RefreshModal which polls the
  // system_request status so the user sees a spinner + progress text
  // rather than just a "queued" alert.
  const requestResearch = async () => {
    if (!mint) return;
    if (!isAuthenticated) {
      openLogin('token_page');
      return;
    }
    setRequestingResearch(true);
    try {
      const jwt = localStorage.getItem('deepnets_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
      const resp = await apiFetch(`${getApiHost()}/api/request-token-safety-analysis`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ mint, ...(isEvm ? { chain: parseEvmChain(token?.chain) ?? chainParam } : {}) }),
      });
      if (resp.status === 401) {
        localStorage.removeItem('deepnets_token');
        refreshUser();
        openLogin('token_page');
        return;
      }
      if (resp.status === 402) {
        const j = await resp.json().catch(() => ({} as any));
        alert(`Insufficient credits. You need 1 but have ${j.available ?? 0}.`);
        return;
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      // Stash the credit value as it was *before* the deduction so
      // RefreshModal can animate the drop.
      const creditsAfter = data.creditsRemaining ?? null;
      setPreviousCredits(creditsAfter != null ? creditsAfter + 1 : null);
      setCurrentRequestId(data.requestId ?? null);
      setRefreshModalOpen(true);
      refreshUser();
    } catch (err) {
      alert(`Failed to request research: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRequestingResearch(false);
    }
  };

  const handleRefreshModalClose = () => {
    setRefreshModalOpen(false);
    setCurrentRequestId(null);
    setPreviousCredits(null);
  };

  const handleRefreshSuccess = () => {
    // Reload to show fresh analysis — same behavior as V1.
    window.location.reload();
  };

  const handleRefreshError = (message: string) => {
    alert(`Analysis failed: ${message}`);
  };

  // Single fetch: the safety record carries everything the stat strip
  // needs (holders, top-holder %, top-network %, top-10 %). Live price
  // and market cap are intentionally NOT shown in the strip — the
  // GMGN chart below already shows current price/MC and we don't want
  // a stale stat number sitting next to a live candle.
  useEffect(() => {
    if (!mint) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // Attach the user's JWT so the API can resolve their tier and
        // skip the new-watchlist research-gate. Without this, even a
        // signed-in Watcher/Deep Diver looks anonymous to the server
        // and gets stripped fields, which V2 then rendered as zeros.
        const headers: Record<string, string> = {};
        const jwt = localStorage.getItem('deepnets_token');
        if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
        setChainCandidates(null);
        if (routeChain && !isEvm) { setError('Not a valid token address for this chain.'); return; }
        if (routeChain && mint !== mint.toLowerCase()) { navigate(tokenPath(mint, routeChain), { replace: true }); return; }
        if (legacyChain) { navigate(tokenPath(mint, legacyChain), { replace: true }); return; }
        if (isEvm && !chainParam) {
          const rr = await apiFetch(`${getApiHost()}/api/resolve-token?address=${mint}`);
          if (!rr.ok) throw new Error(`resolve HTTP ${rr.status}`);
          const chains: ChainCandidate[] = (await rr.json())?.chains ?? [];
          if (cancelled) return;
          if (chains.length === 1) { navigate(tokenPath(mint, chains[0].chain), { replace: true }); return; }
          if (chains.length > 1) { setChainCandidates(chains); return; }
          setError('No trading pairs found for this address on Robinhood Chain, Base, Ethereum, BNB Chain or Arc.');
          return;
        }
        const safetyResp = await apiFetch(`${getApiHost()}/api/token-safety/${mint}${chainQs}`, { headers });
        if (safetyResp.status === 404) {
          // A valid mint we simply haven't researched yet carries a `hint`. Flag it
          // so we auto-run a quick analysis instead of erroring out.
          const body = await safetyResp.json().catch(() => ({} as any));
          if (cancelled) return;
          if ((body as any)?.hint) { setUnresearched(true); return; }
        }
        if (!safetyResp.ok) throw new Error(`safety HTTP ${safetyResp.status}`);
        const safety = await safetyResp.json();
        if (cancelled) return;
        setToken(safety);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mint, chainQs, legacyChain]); // eslint-disable-line react-hooks/exhaustive-deps

  // Unresearched valid token → kick off a quick (no-social, FREE) analysis and poll
  // for the result. The request endpoint is authed, so this runs for signed-in users;
  // anonymous visitors get a sign-in prompt (handled in render).
  useEffect(() => {
    if (!unresearched || !mint || !isAuthenticated || autoResearchRef.current) return;
    autoResearchRef.current = true;
    let cancelled = false;
    (async () => {
      const jwt = localStorage.getItem('deepnets_token');
      const postHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      if (jwt) postHeaders['Authorization'] = `Bearer ${jwt}`;
      let requestId: string | null = null;
      try {
        const resp = await apiFetch(`${getApiHost()}/api/request-token-safety-analysis`, {
          method: 'POST', headers: postHeaders, body: JSON.stringify({ mint, skipSocialResearch: true, ...(chainParam ? { chain: chainParam } : {}) }),
        });
        if (resp.ok) requestId = (await resp.json())?.requestId ?? null;
      } catch { /* fall through to polling — it may already be mid-analysis */ }
      const getHeaders: Record<string, string> = jwt ? { Authorization: `Bearer ${jwt}` } : {};
      const showResult = async () => {
        const r = await apiFetch(`${getApiHost()}/api/token-safety/${mint}${chainQs}`, { headers: getHeaders });
        if (r.ok && !cancelled) { setToken(await r.json()); setUnresearched(false); return true; }
        return false;
      };
      for (let i = 0; i < 45 && !cancelled; i++) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          if (requestId) {
            const s = await apiFetch(`${getApiHost()}/api/request-status?requestId=${requestId}`);
            if (s.ok) {
              const sd = await s.json();
              if (sd.status === 'completed') { await showResult(); return; }
              if (sd.status === 'failed') {
                if (!cancelled) setAnalyzeError(sd.failureReason || 'This token could not be analyzed.');
                return;
              }
            }
          } else if (await showResult()) { // no requestId (e.g. anon) — just watch for the record
            return;
          }
        } catch { /* keep polling */ }
      }
      if (!cancelled) setAnalyzeError('Analysis is taking longer than expected — please refresh in a moment.');
    })();
    return () => { cancelled = true; };
  }, [unresearched, isAuthenticated, mint, chainQs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch supply lock + staking summaries in parallel; both endpoints
  // 200 with zeroes when nothing is locked/staked, so we just stash
  // the percent into state. Pills only render when > 0.
  useEffect(() => {
    if (!mint || isEvm) return; // Streamflow/Tibane are Solana programs
    let cancelled = false;
    const host = getApiHost();
    apiFetch(`${host}/api/streamflow-locks/${mint}`)
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (!cancelled && j) setLockedPercent(j.totalLockedPercent ?? 0); })
      .catch(() => {});
    apiFetch(`${host}/api/tibane-staking/${mint}`)
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (!cancelled && j) setStakedPercent(j.totalStakedPercent ?? 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [mint, isEvm]);

  if (loading) return <Shell><Centered>Loading…</Centered></Shell>;
  if (chainCandidates) return (
    <Shell>
      <Centered>
        <div className="flex flex-col items-center gap-3 text-gray-400 w-full max-w-md">
          <div className="text-white font-medium">This address trades on more than one chain</div>
          <div className="text-xs text-gray-500 text-center">Pick the chain to analyze. Scammers often deploy lookalikes at the same address on other chains, so check the one you mean.</div>
          <div className="flex flex-col gap-2 w-full mt-1">
            {chainCandidates.map(c => (
              <button key={c.chain} onClick={() => navigate(tokenPath(mint!, c.chain))}
                className="flex items-center gap-3 w-full bg-crypto-card border border-gray-700 hover:border-gray-500 rounded-lg px-3 py-2 text-left transition">
                <span className={`text-[10px] font-semibold border px-1.5 py-0.5 rounded ${EVM_CHAIN_INFO[c.chain]?.badgeClass ?? 'text-gray-300 border-gray-600'}`}>{EVM_CHAIN_INFO[c.chain]?.short ?? c.chain}</span>
                <span className="text-white font-medium">{c.symbol ? `$${c.symbol}` : c.chainName}</span>
                {c.name && <span className="text-gray-500 text-sm truncate">{c.name}</span>}
                <span className="ml-auto text-xs text-gray-400 font-mono whitespace-nowrap">{formatUsdMcap(c.liquidityUsd)} liq</span>
              </button>
            ))}
          </div>
        </div>
      </Centered>
    </Shell>
  );
  if (unresearched && !error) return (
    <Shell>
      <Centered>
        {analyzeError ? (
          <div className="flex flex-col items-center gap-3 text-gray-400 max-w-md text-center">
            <i className="fas fa-triangle-exclamation text-3xl text-crypto-yellow" />
            <div className="text-white font-medium">Can't analyze this token yet</div>
            <div className="text-xs text-gray-500">{analyzeError}</div>
          </div>
        ) : isAuthenticated ? (
          <div className="flex flex-col items-center gap-3 text-gray-400 max-w-md text-center">
            <i className="fas fa-circle-notch fa-spin text-3xl text-crypto-accent" />
            <div className="text-white font-medium">Analyzing this token…</div>
            <div className="text-xs text-gray-500">Running a quick safety scan — holders, distribution, and on-chain risks. This usually takes a few seconds.</div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 text-gray-400 max-w-md text-center">
            <i className="fas fa-magnifying-glass-chart text-3xl text-gray-500" />
            <div className="text-white font-medium">This token hasn't been analyzed yet.</div>
            <button onClick={() => openLogin('token_page')} className="bg-crypto-accent text-black font-semibold rounded-lg px-4 py-2 hover:opacity-90">Sign in to analyze it</button>
          </div>
        )}
      </Centered>
    </Shell>
  );
  if (error || !token) return <Shell><Centered>Failed to load: {error ?? 'no data'}</Centered></Shell>;

  const evmChain = isEvm ? (parseEvmChain(token.chain) ?? chainParam) : null;
  const evmInfo = evmChain ? EVM_CHAIN_INFO[evmChain] : null;
  const chartUrl = evmChain
    ? evmChartUrl(evmChain, token.mint, getChartInterval(token.tokenCreationDate))
    : `https://www.gmgn.cc/kline/sol/${token.mint}?theme=dark&interval=${getChartInterval(token.tokenCreationDate)}&ref=9E9bbGF8`;
  const holderTo = holderAnalysisPath(token.mint, evmChain);
  const safetyLevel = token.overallSafetyLevel;
  const isClean = safetyLevel === 'OK' || safetyLevel === 'SAFE';
  const riskColor = getRiskColor(safetyLevel);
  const infoCount = token.infoMessages?.length ?? 0;
  const warningCount = (token.warnings?.length ?? 0) + (token.criticalRisks?.length ?? 0);
  const tokenAge = token.tokenCreationDate ? formatTimeAgo(token.tokenCreationDate) : null;
  const description = token.metadata?.description;
  const imageUrl = token.metadata?.image;

  return (
    <Shell>
      <SocialMetaTags
        title={`$${token.tokenSymbol} — ${isClean ? 'Clean' : safetyLevel}`}
        description={description || `${token.tokenName} token analysis on deepnets.ai`}
        image={imageUrl || 'https://deepnets.ai/logo512.png'}
        url={`https://deepnets.ai${tokenPath(token.mint, evmChain)}`}
      />
      <main className="max-w-[1600px] w-full mx-auto px-4 py-3 space-y-2">
        {/* HERO STRIP — responsive:
            - narrow: image+title block stacks above a full-width
              controls row, pills and meta wrap cleanly below the name
            - wide: the left (image+title+pills+meta) and right
              (controls) blocks sit side by side, no truncation
            Using items-start (not items-center) keeps the image pinned
            to the top so it doesn't drift vertically when the middle
            grows with pills/meta wrapping. */}
        <div className="bg-crypto-card border border-gray-800 rounded-xl p-3">
          {/* HERO + STATS — single card, two-column on lg+:
              LEFT col = identity flex (image + title/pills/meta).
              RIGHT col = page actions (social icons, Holder Graph,
                          Spotlight, Share) on top + 2x2 stats grid
                          below. The dedicated stats row that used to
                          sit underneath the hero is gone — that band
                          is reclaimed. Description and freshness
                          strip span full width below the grid. */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-x-4 gap-y-3 items-start">
            <div className="min-w-0">
            <div className="flex items-start gap-4 min-w-0">
              <TokenIcon
                src={imageUrl}
                symbol={token.tokenSymbol}
                name={token.tokenName}
                sizeClass="w-14 h-14"
                className="flex-shrink-0"
              />
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <h1 className="text-2xl font-bold leading-tight">${token.tokenSymbol}</h1>
                  <span className="text-gray-400 text-base">{token.tokenName}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <SafetyBadge
                    level={safetyLevel}
                    warningCount={warningCount}
                    infoCount={infoCount}
                    onClick={(warningCount > 0 || infoCount > 0) ? () => {
                      const el = document.getElementById('safety-details');
                      if (!el) return;
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      // Flash the section so the tap has a visible result (people
                      // were rage-clicking the badge when the scroll wasn't obvious).
                      el.style.transition = 'box-shadow 0.25s ease';
                      el.style.boxShadow = '0 0 0 2px rgba(0, 255, 136, 0.6)';
                      window.setTimeout(() => { el.style.boxShadow = ''; }, 1200);
                    } : undefined}
                  />
                  {evmInfo && (
                    <span className={`text-[10px] font-semibold border px-1.5 py-0.5 rounded ${evmInfo.badgeClass}`} title={`${evmInfo.name} token`}>{evmInfo.short}</span>
                  )}
                  {token.platform && (
                    <span className="text-[10px] font-semibold text-gray-300 bg-gray-800 border border-gray-700 px-1.5 py-0.5 rounded" title={`Launched on ${token.platform}`}>{token.platform}</span>
                  )}
                  {token.isOnWatchlist && (() => {
                    const addedAt = token.watchlistedAt ? new Date(token.watchlistedAt) : null;
                    const addedMcap = token.watchlistedAtMcapUsd ?? null;
                    const multiplier = token.watchlistMultiplier ?? null;
                    // A ≥2× run since the add is the headline; show it in the badge
                    // itself so it doesn't take a hover to see.
                    const fmtMult = (m: number) => `${m.toFixed(m >= 100 ? 0 : m >= 10 ? 1 : 2)}×`;
                    const showMult = multiplier != null && multiplier >= 2;
                    return (
                      <span className="relative group/wl">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-crypto-accent bg-crypto-accent/10 border border-crypto-accent/30 px-2 py-0.5 rounded cursor-help">
                          WATCHLIST
                          {showMult && <span className="text-green-400 font-mono">{fmtMult(multiplier!)}</span>}
                        </span>
                        {(addedAt || addedMcap != null) && (
                          <span className="absolute left-0 top-full mt-1 z-50 hidden group-hover/wl:block bg-crypto-dark border border-gray-600 rounded-lg p-2.5 text-left text-xs whitespace-nowrap shadow-xl min-w-[200px]">
                            {addedAt && (
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-500">Added</span>
                                <span className="text-white">
                                  {addedAt.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                </span>
                              </div>
                            )}
                            {addedMcap != null && (
                              <div className="flex justify-between gap-3">
                                <span className="text-gray-500">Mcap then</span>
                                <span className="text-white font-mono">{formatUsdMcap(addedMcap)}</span>
                              </div>
                            )}
                            {multiplier != null && multiplier > 1 && (
                              <div className="flex justify-between gap-3 mt-1 pt-1 border-t border-gray-700">
                                <span className="text-gray-500">Now</span>
                                <span className="text-green-400 font-medium">{fmtMult(multiplier)}</span>
                              </div>
                            )}
                          </span>
                        )}
                      </span>
                    );
                  })()}
                  {token.isSpotlighted && (
                    <span className="text-[10px] font-semibold text-crypto-yellow bg-crypto-yellow/10 border border-crypto-yellow/30 px-1.5 py-0.5 rounded">★ SPOTLIGHT</span>
                  )}
                  {token.charity && (() => {
                    const share = token.charity.donationSharePct;
                    const donated = token.charity.totalDonatedSol;
                    const tooltipBits = [
                      `Charity coin — fees → ${token.charity.name ?? 'charity'}`,
                      share != null ? `${share}% of creator fees` : null,
                      donated != null ? `${donated.toFixed(2)} SOL donated to date` : null,
                      token.charity.adminRevoked ? 'recipient locked (admin revoked)' : null,
                    ].filter(Boolean);
                    return (
                      <button
                        onClick={() => {
                          document.getElementById('charity-details')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-pink-300 bg-pink-500/10 border border-pink-500/30 px-1.5 py-0.5 rounded hover:bg-pink-500/20 transition cursor-pointer"
                        title={tooltipBits.join(' • ')}
                      >
                        <i className="fas fa-hand-holding-heart" />
                        CHARITY
                      </button>
                    );
                  })()}
                  {/* Supply-side positive signals — informational pills.
                      The dedicated locked/staked sections were removed,
                      so these are non-interactive (no scroll target). */}
                  {lockedPercent != null && lockedPercent > 0 && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-400 bg-green-500/10 border border-green-500/30 px-2 py-0.5 rounded"
                      title="Token supply locked in Streamflow vesting contracts"
                    >
                      <i className="fas fa-lock text-[9px]" />
                      {lockedPercent.toFixed(1)}% locked
                    </span>
                  )}
                  {stakedPercent != null && stakedPercent > 0 && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-yellow-300 bg-yellow-500/10 border border-yellow-500/30 px-2 py-0.5 rounded"
                      title="Token supply staked via Tibane"
                    >
                      <i className="fas fa-gem text-[9px]" />
                      {stakedPercent.toFixed(1)}% staked
                    </span>
                  )}
                  {token.burnPercent != null && token.burnPercent > 1 && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-300 bg-orange-500/10 border border-orange-500/30 px-2 py-0.5 rounded"
                      title="Supply held at known burn addresses (incinerator + mint)"
                    >
                      <i className="fas fa-fire text-[9px]" />
                      {token.burnPercent.toFixed(1)}% burned
                    </span>
                  )}
                  {token.totalMultisigPercent != null && token.totalMultisigPercent > 0 && (() => {
                    const detail =
                      token.multisigVaultCount === 1 && token.multisigThreshold && token.multisigMemberCount
                        ? ` (${token.multisigThreshold}-of-${token.multisigMemberCount})`
                        : token.multisigVaultCount && token.multisigVaultCount > 1
                          ? ` across ${token.multisigVaultCount} vaults`
                          : '';
                    return (
                      <span
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded"
                        title={`Token supply held in Squads V4 multisig vaults (threshold ≥ 2 — public shared-control wallets)${detail}`}
                      >
                        <i className="fas fa-shield-halved text-[9px]" />
                        {token.totalMultisigPercent.toFixed(1)}% multisig{detail}
                      </span>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-x-2 gap-y-1 text-[12px] text-gray-500 mt-1.5 flex-wrap">
                  {/* The whole address is one big tap-to-copy target (copying the CA
                      is the #1 mobile action) — the old 10px copy icon was too small. */}
                  <CopyButton
                    text={token.mint}
                    title="Copy contract address"
                    iconClassName="text-[11px]"
                    className="font-mono text-[12px] text-gray-300 hover:text-white bg-crypto-dark/60 hover:bg-crypto-dark border border-gray-700 rounded-md px-2.5 py-1.5 active:scale-95 transition"
                  >
                    <span>{token.mint.slice(0, 4)}…{token.mint.slice(-4)}</span>
                  </CopyButton>
                  <a href={evmInfo ? `${evmInfo.explorer}/token/${token.mint}` : `https://solscan.io/token/${token.mint}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 p-1.5" title={evmInfo ? `View on ${evmInfo.name} explorer` : 'View on Solscan'}><i className="fas fa-external-link-alt text-[10px]" /></a>
                  {tokenAge && <><span className="text-gray-700">·</span><span>Created {tokenAge}</span></>}
                  {evmInfo && token.totalLiquidityUsd != null && (
                    <><span className="text-gray-700">·</span><span title="DEX pool liquidity at analysis time">{formatUsdMcap(token.totalLiquidityUsd)} liquidity</span></>
                  )}
                  {token.creator && (
                    <>
                      <span className="text-gray-700">·</span>
                      <span>Creator <Link to={walletPath(token.creator, evmChain)} className="font-mono text-blue-400 hover:text-blue-300">{token.creator.slice(0, evmInfo ? 6 : 4)}…{token.creator.slice(-4)}</Link></span>
                      {token.creatorFigure ? (
                        <FigureBadge figureKey={token.creatorFigure.figureKey} displayName={token.creatorFigure.displayName} trustLevel={token.creatorFigure.trustLevel} confidence={token.creatorFigure.confidence} className="text-[12px]" />
                      ) : token.creatorLabel ? (
                        <span className="text-[12px] text-gray-300 bg-gray-800 border border-gray-700 rounded px-1.5 py-0.5">{token.creatorLabel}</span>
                      ) : null}
                      {typeof token.creatorTokenCount === 'number' && token.creatorTokenCount > 0 && (
                        <span className="text-gray-600">({token.creatorTokenCount} other token{token.creatorTokenCount === 1 ? '' : 's'} by this creator)</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            {description && (
              <p className="text-gray-400 text-sm mt-2 line-clamp-2">{description}</p>
            )}
            {token.figureHolders && token.figureHolders.length > 0 && (
              <div className="mt-2 flex items-center gap-x-3 gap-y-1 flex-wrap text-sm">
                <span className="text-gray-500"><i className="fas fa-users mr-1.5" />Known figures holding:</span>
                {token.figureHolders.map(f => (
                  <FigureBadge key={f.figureKey} figureKey={f.figureKey} displayName={f.displayName} trustLevel={f.trustLevel} confidence={f.confidence} percentage={f.percentage} sent={f.sent} tokenAccount={f.tokenAccount} />
                ))}
              </div>
            )}
            {(() => {
              const analysisAgeMs = Date.now() - new Date(token.timestamp).getTime();
              const isStale = analysisAgeMs > 60 * 60 * 1000;
              return (
                <div className="mt-2 pt-2 border-t border-gray-800 flex items-center flex-wrap gap-3 text-[12px]">
                  <span className={`flex items-center gap-1.5 ${isStale ? 'text-gray-300' : 'text-gray-500'}`}>
                    <i className="fas fa-clock" />
                    Analysis from <span className="font-medium">{formatTimeAgo(token.timestamp)}</span>
                    {isStale && <span className="text-gray-500">(may be outdated)</span>}
                  </span>
                  <button onClick={requestResearch} disabled={requestingResearch} className={`px-2.5 py-1 font-medium rounded-lg border transition disabled:opacity-50 ${isStale ? 'border-gray-600 text-gray-300 hover:text-white hover:border-gray-500' : 'border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-600'}`} title={isStale ? 'Re-run safety + social research (costs 1 credit)' : 'Force a re-run even though this analysis is still fresh (costs 1 credit)'}>
                    {requestingResearch ? 'Requesting…' : (token.hasSocialResearch || evmInfo ? 'Refresh (1 credit)' : 'Full Research (1 credit)')}
                  </button>
                  <Link to={`${tokenPath(token.mint, evmChain)}/safety-history`} className="flex items-center gap-1.5 px-2.5 py-1 font-medium rounded-lg border border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-600 transition" title="View this token's past safety analyses">
                    <i className="fas fa-history" /><span>History</span>
                  </Link>
                </div>
              );
            })()}
            </div>
            {/* RIGHT col: on wide screens, actions on top + stats grid below.
                On narrow, the column collapses below the identity block — flip
                the order with flex-col-reverse so the stats are visible
                without scrolling past the action row first. */}
            <div className="flex flex-col-reverse lg:flex-col gap-3 min-w-0">
              <div className="flex items-center gap-2 flex-wrap lg:justify-end">
                {(token.metadata?.website) && <a href={token.metadata.website} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white px-1" title="Website"><i className="fas fa-globe" /></a>}
                {(token.metadata?.twitter) && <a href={token.metadata.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white px-1" title="Twitter"><i className="fab fa-x-twitter" /></a>}
                {(token.metadata?.telegram) && <a href={token.metadata.telegram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white px-1" title="Telegram"><i className="fab fa-telegram" /></a>}
                {/* Holder Graph link moved into the stats grid below
                    so it's grouped with the other holder navigation. */}
                {/* Spotlight CTA — same gating as V1: clean + watchlisted + not already lit. */}
                {!evmInfo && token.isOnWatchlist && !token.isSpotlighted && isClean && (
                  <a
                    href={`/?spotlight=open&mint=${token.mint}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-crypto-yellow/40 text-crypto-yellow hover:bg-crypto-yellow/10 transition-colors"
                    title="Sponsor this token as a Spotlight"
                  >
                    <i className="fas fa-star text-[10px]" />
                    Spotlight It
                  </a>
                )}
                <ShareButton mint={token.mint} tokenDetails={token} pagePath={tokenPath(token.mint, evmChain)} />
              </div>
              {token.researchGated ? (
                <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
                  <div className="flex items-start gap-2">
                    <i className="fas fa-lock text-yellow-300 mt-0.5" />
                    <div className="flex-1 min-w-0 text-xs">
                      <div className="font-semibold text-sm mb-0.5">Holder research is locked</div>
                      <div className="text-gray-500 mb-2">2h gate after watchlisting, or Watcher+ tier.</div>
                      <button onClick={requestResearch} disabled={requestingResearch} className="px-3 py-1 text-xs font-semibold rounded border border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10 transition disabled:opacity-50">
                        {requestingResearch ? 'Unlocking…' : 'Unlock (1 credit)'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // 3-col grid: 4 numerical stat cards in cols 1-2, two
                // navigation cards (Holder Graph, Full Analysis) in
                // col 3 — same physical card style as the stats so the
                // group reads as one cohesive concentration block.
                <div className="grid grid-cols-3 gap-2">
                  <StatCard label="Holders" value={(token.totalHolders ?? 0).toLocaleString()} to={holderTo} />
                  <StatCard label="Top holder" value={`${(token.topHolderOwnership ?? 0).toFixed(1)}%`} valueColor={statColor(token.topHolderRiskLevel)} to={holderTo} />
                  {<GatedLink
                    to={`${tokenPath(token.mint, evmChain)}/network-graph`}
                    onClick={() => trackEvent('network_graph_opened', { mint: token.mint, source: 'v2_stats' })}
                    className="group bg-blue-500/5 border border-blue-500/30 hover:bg-blue-500/10 hover:border-blue-500/60 rounded-xl px-3 py-2 transition-colors"
                    title="View the funding-source graph for this token's holders"
                  >
                    <div className="text-base font-semibold leading-tight text-blue-300">
                      <i className="fas fa-project-diagram" />
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-blue-300 mt-0.5 flex items-center justify-between">
                      <span>Holder Graph</span>
                      <i className="fas fa-arrow-right text-[9px] opacity-60 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </GatedLink>}
                  <StatCard label="Top 10" value={`${(token.topTenOwnership ?? 0).toFixed(1)}%`} valueColor={statColor(token.topTenHoldersRiskLevel)} to={holderTo} />
                  <StatCard label="Top network" value={token.topNetworkOwnership > 0 ? `${token.topNetworkOwnership.toFixed(1)}% / ${token.topNetworkWalletCount}` : '—'} valueColor={statColor(token.topNetworkRiskLevel)} to={holderTo} />
                  {<GatedLink
                    to={holderTo}
                    className="group bg-purple-500/5 border border-purple-500/30 hover:bg-purple-500/10 hover:border-purple-500/60 rounded-xl px-3 py-2 transition-colors"
                    title="Top wallets, funding networks, exchange distribution, rugger tags"
                  >
                    <div className="text-base font-semibold leading-tight text-purple-300">
                      <i className="fas fa-users" />
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-purple-300 mt-0.5 flex items-center justify-between">
                      <span>Full Analysis</span>
                      <i className="fas fa-arrow-right text-[9px] opacity-60 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </GatedLink>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats used to live here as a standalone row; now folded into
            the hero's right column above. */}

        {/* CHART + BUY — mirrors the hero's minmax(0,1fr)_420px split so
            the swap panel stays exactly as wide as the holder-stats grid
            above it and doesn't eat chart space on wide screens.
            On mobile (default): chart + a single Trade CTA below it.
            Tapping Trade reveals the SwapPanel and hides everything
            below — see mobileTradeOpen. */}
        {/* EVM tokens: chart only — the swap panel trades Solana via Jupiter. */}
        <div className={`grid grid-cols-1 gap-3 ${evmInfo ? '' : 'lg:grid-cols-[minmax(0,1fr)_420px]'}`}>
          <div className="bg-crypto-card rounded-xl overflow-hidden min-w-0">
            <iframe
              src={chartUrl}
              title="Price Chart"
              className="w-full h-[480px] border-0"
              allow="clipboard-write"
            />
          </div>

          {/* Mobile-only Trade CTA. If the user is authenticated AND
              has a working in-page sign path (Phantom deeplink session
              or live WC session matching their auth wallet), just open
              the swap overlay — SwapPanel will route the actual sign
              through the deeplink/WC flow. Otherwise fall back to the
              wallet picker (sign-in for unauth'd users) or Phantom's
              in-app browser. */}
          {!evmInfo && <>
          <button
            type="button"
            onClick={() => {
              const authWallet = user?.walletAddress ?? null;
              const hasSignPath =
                isAuthenticated && !!authWallet &&
                (persistentPhantomWalletMatches(authWallet) || hasLiveWCSessionSync(authWallet));
              if (hasSignPath) {
                setMobileTradeOpen(true);
                return;
              }
              if (redirectToPhantomIfMobile('swap')) return;
              setMobileTradeOpen(true);
            }}
            className="lg:hidden w-full border border-green-500 text-green-400 hover:bg-green-500/10 font-semibold text-base py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <i className="fas fa-arrow-right-arrow-left text-sm" />
            Trade ${token.tokenSymbol}
          </button>

          {/* Swap panel: always visible on lg+. Mobile shows the
              fullscreen overlay instead (rendered at the end of this
              component). */}
          <div id="swap" className="scroll-mt-20 hidden lg:block">
            <SwapPanel tokenMint={token.mint} tokenSymbol={token.tokenSymbol} riskLevel={token.overallSafetyLevel} />
          </div>
          </>}
        </div>

        {/* SAFETY DETAILS — collapsed to compact lists, not a giant box */}
        {(warningCount > 0 || infoCount > 0) && (
          <div id="safety-details" className="bg-crypto-card border border-gray-800 rounded-xl p-3 scroll-mt-20">
            <h2 className="font-semibold mb-2 flex items-center gap-2 text-sm">
              <i className={`fas ${isClean ? 'fa-check-circle' : 'fa-exclamation-triangle'} text-${riskColor}`} />
              Safety details
            </h2>
            <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm [&>div]:flex-1 [&>div]:min-w-[180px]">
              <SafetyList title="Critical" icon="fa-circle-exclamation" color="text-crypto-red" items={token.criticalRisks} />
              <SafetyList title="Warnings" icon="fa-triangle-exclamation" color="text-crypto-yellow" items={token.warnings} />
              <SafetyList title="Info" icon="fa-info-circle" color="text-blue-300" items={token.infoMessages ?? []} />
            </div>
            <div className="mt-2 pt-2 border-t border-gray-800 text-xs text-gray-500 flex items-center gap-3 flex-wrap">
              <span>Analyzed {formatTimeAgo(token.timestamp)}</span>
              <span className="text-gray-700">·</span>
              {/* EVM: no freeze authority concept; "mint frozen" reads as Solana jargon. */}
              <span>{token.isMintable ? '⚠ mintable' : evmInfo ? 'supply fixed' : 'mint frozen'}</span>
              {!evmInfo && <>
                <span className="text-gray-700">·</span>
                <span>{token.isFreezable ? '⚠ freezable' : 'no freeze authority'}</span>
              </>}
            </div>
          </div>
        )}

        {/* CHARITY — full breakdown of the on-chain fee-routing setup
            (charity name + logo, donation pool, lifetime SOL routed,
            and whether the creator has revoked admin rights so the
            recipient can't be changed). Only shown for tokens we've
            enriched from pump.fun's sharing-config. */}
        {token.charity && (token.charity.totalDonatedSol != null || token.charity.adminRevoked != null || token.charity.beneficiaryWallet) && (
          <div id="charity-details" className="bg-crypto-card border border-pink-500/20 rounded-xl p-3 scroll-mt-20">
            <h2 className="font-semibold mb-2 flex items-center gap-2 text-sm">
              <i className="fas fa-hand-holding-heart text-pink-400" />
              Charity
            </h2>
            <div className="flex items-start gap-3 flex-wrap">
              {token.charity.logo && (
                <img src={token.charity.logo} alt={token.charity.name ?? 'charity logo'}
                  className="w-12 h-12 rounded bg-white flex-shrink-0 object-contain p-1"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              )}
              <div className="flex-1 min-w-0 text-sm">
                <div className="flex items-baseline gap-2 flex-wrap">
                  {token.charity.website ? (
                    <a href={token.charity.website} target="_blank" rel="noopener noreferrer"
                      className="font-semibold text-pink-300 hover:text-pink-200">
                      {token.charity.name ?? 'Charity'}
                    </a>
                  ) : (
                    <span className="font-semibold text-pink-300">{token.charity.name ?? 'Charity'}</span>
                  )}
                  {token.charity.country && (
                    <span className="text-xs text-gray-500">{token.charity.country}</span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                  {token.charity.donationSharePct != null && (
                    <span><span className="text-gray-500">Fee share to charity:</span> <span className="text-gray-200 font-medium">{token.charity.donationSharePct}%</span></span>
                  )}
                  {token.charity.totalDonatedSol != null && (
                    <span><span className="text-gray-500">Donated to date:</span> <span className="text-gray-200 font-medium">{token.charity.totalDonatedSol.toFixed(3)} SOL</span></span>
                  )}
                  {token.charity.adminRevoked === true && (
                    <span className="text-green-400" title="The creator has permanently given up the right to change the recipient.">
                      <i className="fas fa-lock mr-1 text-[10px]" />Recipient locked
                    </span>
                  )}
                  {token.charity.adminRevoked === false && (
                    <span className="text-yellow-400" title="The creator can still change where the fees go.">
                      <i className="fas fa-unlock mr-1 text-[10px]" />Recipient not locked
                    </span>
                  )}
                </div>
                {token.charity.charities && token.charity.charities.length > 1 && (
                  <div className="mt-2 text-xs text-gray-500">
                    Multi-charity split:{' '}
                    {token.charity.charities.map((ch, i) => (
                      <span key={ch.slug ?? i}>
                        {i > 0 && ', '}
                        {ch.name} ({ch.weight}%)
                      </span>
                    ))}
                  </div>
                )}
                {(token.charity.beneficiaryWallet || token.charity.sharingConfigPda) && (
                  <div className="mt-2 text-[11px] text-gray-600 font-mono break-all">
                    {token.charity.beneficiaryWallet && (
                      <div>donation pool: {token.charity.beneficiaryWallet}</div>
                    )}
                    {token.charity.sharingConfigPda && (
                      <div>config PDA: {token.charity.sharingConfigPda}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* SOCIAL — both child components are self-contained cards. */}
        {/* Social research runs on the Solana pipeline only; EVM requests re-run safety. */}
        {mint && !evmInfo && <SocialResearchSummary mint={mint} onRequestResearch={requestResearch} requestingResearch={requestingResearch} />}
        {mint && !evmInfo && <ShillTweets mint={mint} />}

        {/* LIQUIDITY (usually a one-liner) */}
        {token.liquidityAnalysis && (
          <div className="bg-crypto-card border border-gray-800 rounded-xl p-3">
            <h2 className="font-semibold mb-2 flex items-center gap-2 text-sm"><i className="fas fa-water text-blue-400" />Liquidity</h2>
            <p className="text-gray-300 text-sm">{token.liquidityAnalysis}</p>
          </div>
        )}
      </main>
      <Footer />
      <RefreshModal
        isOpen={refreshModalOpen}
        requestId={currentRequestId}
        previousCredits={previousCredits}
        currentCredits={user?.credits ?? null}
        onClose={handleRefreshModalClose}
        onSuccess={handleRefreshSuccess}
        onError={handleRefreshError}
      />
      {mobileTradeOpen && !evmInfo && (
        <MobileTradeOverlay
          mint={token.mint}
          symbol={token.tokenSymbol}
          chartUrl={chartUrl}
          riskLevel={token.overallSafetyLevel}
          onClose={() => {
            setMobileTradeOpen(false);
            if (window.location.hash === '#swap') {
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
          }}
        />
      )}
    </Shell>
  );
};

/**
 * Fullscreen mobile trade UI: chart fills the remaining height with a
 * floating X close in the top-right corner; the SwapPanel sits at the
 * bottom in a fixed-height drawer. Locks page scroll while open. Only
 * mounted on mobile (the parent already gates this behind a Trade
 * button that's mobile-only). Uses fixed inset-0 z-50 so it sits over
 * the Header, Footer, and main page content without re-mounting them.
 */
const MobileTradeOverlay: React.FC<{
  mint: string;
  symbol: string;
  chartUrl: string;
  riskLevel: TokenSafety['overallSafetyLevel'];
  onClose: () => void;
}> = ({ mint, symbol, chartUrl, riskLevel, onClose }) => {
  // Lock body scroll while open. Restore on unmount.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div className="fixed inset-0 z-[60] bg-crypto-dark flex flex-col lg:hidden">
      {/* Chart — flex-1 so it fills whatever's left after the swap drawer. */}
      <div className="flex-1 relative bg-black min-h-0">
        <iframe
          src={chartUrl}
          title={`${symbol} chart`}
          className="absolute inset-0 w-full h-full border-0"
          allow="clipboard-write"
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close trade view"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center hover:bg-black/80 transition-colors z-10 shadow-lg"
          style={{ top: 'max(0.75rem, env(safe-area-inset-top))', right: 'max(0.75rem, env(safe-area-inset-right))' }}
        >
          <i className="fas fa-times text-base" />
        </button>
      </div>
      {/* Swap drawer — FIXED height so the chart's flex-1 allocation
          stays stable across SwapPanel's state changes (no-quote →
          fetching → quote present). Earlier we used max-height which
          made the drawer shrink/grow as quote text appeared/disappeared
          and the chart visibly jumped. A fixed 360px fits both states;
          internal overflow-y-auto handles the slippage-expanded edge
          case without resizing the chart. */}
      <div
        className="bg-crypto-card border-t border-gray-700 overflow-y-auto flex-shrink-0"
        style={{ height: '300px', paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <SwapPanel tokenMint={mint} tokenSymbol={symbol} riskLevel={riskLevel} compact />
      </div>
    </div>
  );
};

// ── Small helpers (inline to keep V2 self-contained) ─────────────────────

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
    <Header />
    {children}
  </div>
);

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <main className="flex-1 flex items-center justify-center text-gray-400">{children}</main>
);

const StatCard: React.FC<{ label: string; value: React.ReactNode; valueColor?: string; to?: string }> = ({ label, value, valueColor, to }) => {
  const body = (
    <>
      <div className={`text-base font-semibold leading-tight ${valueColor ?? 'text-white'}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-0.5 flex items-center justify-between">
        <span>{label}</span>
        {to && <i className="fas fa-arrow-right text-[9px] opacity-0 group-hover:opacity-60 transition-opacity" />}
      </div>
    </>
  );
  if (to) {
    return (
      <GatedLink to={to} className="group bg-crypto-card border border-gray-800 hover:border-gray-600 rounded-xl px-3 py-2 transition-colors">
        {body}
      </GatedLink>
    );
  }
  return (
    <div className="bg-crypto-card border border-gray-800 rounded-xl px-3 py-2">
      {body}
    </div>
  );
};

const SafetyBadge: React.FC<{ level: string; warningCount: number; infoCount: number; onClick?: () => void }> = ({ level, warningCount, infoCount, onClick }) => {
  const isClean = level === 'OK' || level === 'SAFE';
  const cls = isClean
    ? 'text-crypto-accent bg-crypto-accent/10 border-crypto-accent/30'
    : level === 'RISKY'
    ? 'text-crypto-yellow bg-crypto-yellow/10 border-crypto-yellow/30'
    : 'text-crypto-red bg-crypto-red/10 border-crypto-red/30';
  const label = isClean ? 'Clean' : level;
  const inner = (
    <>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />{label}
      {warningCount > 0 && <span className="text-[10px] opacity-80">· {warningCount} {warningCount === 1 ? 'risk' : 'risks'}</span>}
      {infoCount > 0 && <span className="text-[10px] opacity-80">· {infoCount} info</span>}
    </>
  );
  const base = `inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded border ${cls}`;
  if (onClick) {
    // Chevron makes it read as tappable — the plain badge gave no cue (esp. on
    // mobile with no hover), so users rage-clicked it expecting it to expand.
    return (
      <button type="button" onClick={onClick} className={`${base} hover:brightness-125 transition cursor-pointer`} title="Tap for safety details">
        {inner}
        <i className="fas fa-chevron-down text-[9px] opacity-70 ml-0.5" />
      </button>
    );
  }
  return <span className={base}>{inner}</span>;
};

const SafetyList: React.FC<{ title: string; icon: string; color: string; items: string[] }> = ({ title, icon, color, items }) => {
  // Empty categories are hidden entirely so a clean token with only
  // an Info message doesn't eat three columns of "none" placeholders.
  if (!items || items.length === 0) return null;
  return (
    <div>
      <h3 className={`text-sm font-semibold flex items-center gap-1.5 ${color} mb-2`}><i className={`fas ${icon}`} />{title}</h3>
      <ul className="space-y-1">
        {items.map((s, i) => <li key={i} className="text-gray-300 text-xs flex gap-1.5"><span className="text-gray-600">·</span><span>{s}</span></li>)}
      </ul>
    </div>
  );
};


function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

function getChartInterval(creationDate?: string): string {
  if (!creationDate) return '5';
  const ageHours = (Date.now() - new Date(creationDate).getTime()) / 3600_000;
  if (ageHours < 2) return '1';
  if (ageHours < 12) return '5';
  if (ageHours < 48) return '15';
  if (ageHours < 168) return '60';
  return '240';
}

export default TokenDetailsPage;
